import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useModal } from '@ebay/nice-modal-react';
import { compose, path, propOr } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { codelistProvider, ComponentLoader } from 'components/index';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import fetch from 'features/common/fetch';
import { fetchClientById } from 'features/views/clientSearch/actions';
import policySearch from 'features/views/policySearch';
import { setPolicyCompanyId, setSelectedPolicy } from 'features/views/taskCreatingPages/slice';
import { getCodelistPropById } from 'utils';

import ResultsTable from './components/ResultsTable';

export const PolicySearchModalBody = ({ onSelect, policyNo, proposalNo, policyStatusesCodelist }) => {
	const dispatch = useDispatch();
	const policies = useSelector(policySearch.selectors.getRoot);
	const policySearchModal = useModal(MODALS.policy_search);

	useEffect(() => {
		dispatch(policySearch.actions.searchPolicy(policyNo, proposalNo));
		// eslint-disable-next-line
	}, [dispatch]);

	useEffect(
		() => () => {
			dispatch(policySearch.actions.clearSearch());
		},
		[dispatch],
	);

	const handlePolicySelect = useCallback(
		async (event) => {
			event.preventDefault();
			const selectedPolicy = policies[+event.target.dataset.policyId];
			const policyStatus = getCodelistPropById(
				selectedPolicy.policyStatusId,
				policyStatusesCodelist,
				'shortDescription',
			);

			const partnerId = path(['policyHolder', 'id'])(selectedPolicy);
			const companyId = propOr(null, 'instanceCompanyId')(selectedPolicy);
			dispatch(setSelectedPolicy(selectedPolicy));
			dispatch(setPolicyCompanyId(companyId));
			const { body: partner } = await dispatch(fetchClientById(partnerId));
			policySearchModal.hide();
			onSelect(selectedPolicy, policyStatus, partner);
		},
		[dispatch, onSelect, policies, policyStatusesCodelist, policySearchModal],
	);

	const handleOnReload = useCallback(() => {
		dispatch(policySearch.actions.searchPolicy(policyNo, proposalNo));
	}, [dispatch, policyNo, proposalNo]);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.policy} onReload={handleOnReload}>
			<ComponentLoader fetchStorePath={fetch.constants.urls.partner}>
				<ResultsTable policies={policies} onSelect={handlePolicySelect} />
			</ComponentLoader>
		</ComponentLoader>
	);
};

PolicySearchModalBody.propTypes = {
	onSelect: PropTypes.func,
	policyNo: PropTypes.number,
	policyStatusesCodelist: PropTypes.array,
	productsCodelist: PropTypes.array,
	proposalNo: PropTypes.number,
};

export default compose(
	codelistProvider(codelists.policyStatuses, 'policyStatusesCodelist'),
	codelistProvider(codelists.products, 'productsCodelist'),
)(PolicySearchModalBody);
